import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useEffect, useState } from 'react';

interface EventReactionsState {
  favoriteEventIds: string[];
  rsvpEventIds: string[];
  loading: boolean;
  fetchFavoriteEvents: (userId: string) => Promise<void>;
  fetchRsvpEvents: (userId: string) => Promise<void>;
  addFavoriteEvent: (userId: string, eventId: string) => Promise<void>;
  removeFavoriteEvent: (userId: string, eventId: string) => Promise<void>;
  addRsvp: (userId: string, eventId: string) => Promise<void>;
  removeRsvp: (userId: string, eventId: string) => Promise<void>;
  isFavorited: (eventId: string) => boolean;
  isRsvped: (eventId: string) => boolean;
  toggleFavorite: (eventId: string) => Promise<void>;
  toggleRsvp: (eventId: string) => Promise<void>;
  resetState: () => void;
}

const useEventReactionsStore = create<EventReactionsState>((set, get) => ({
  favoriteEventIds: [],
  rsvpEventIds: [],
  loading: false,

  fetchFavoriteEvents: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('favorite_events')
        .select('event_id')
        .eq('user_id', userId);
      if (error) throw error;
      set({ favoriteEventIds: (data ?? []).map((d) => d.event_id) });
    } catch (e) {
      console.error('Error fetching favorite events:', e);
    }
  },

  fetchRsvpEvents: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('event_id')
        .eq('user_id', userId);
      if (error) throw error;
      set({ rsvpEventIds: (data ?? []).map((d) => d.event_id) });
    } catch (e) {
      console.error('Error fetching rsvp events:', e);
    }
  },

  addFavoriteEvent: async (userId: string, eventId: string) => {
    try {
      const { error } = await supabase
        .from('favorite_events')
        .insert([{ user_id: userId, event_id: eventId }]);
      if (error) throw error;
      set((s) => ({ favoriteEventIds: [...s.favoriteEventIds, eventId] }));
    } catch (e) {
      console.error('Error adding favorite event:', e);
    }
  },

  removeFavoriteEvent: async (userId: string, eventId: string) => {
    try {
      const { error } = await supabase
        .from('favorite_events')
        .delete()
        .eq('user_id', userId)
        .eq('event_id', eventId);
      if (error) throw error;
      set((s) => ({
        favoriteEventIds: s.favoriteEventIds.filter((id) => id !== eventId),
      }));
    } catch (e) {
      console.error('Error removing favorite event:', e);
    }
  },

  addRsvp: async (userId: string, eventId: string) => {
    try {
      const { error } = await supabase
        .from('event_rsvps')
        .insert([{ user_id: userId, event_id: eventId }]);
      if (error) throw error;
      set((s) => ({ rsvpEventIds: [...s.rsvpEventIds, eventId] }));
    } catch (e) {
      console.error('Error adding rsvp:', e);
    }
  },

  removeRsvp: async (userId: string, eventId: string) => {
    try {
      const { error } = await supabase
        .from('event_rsvps')
        .delete()
        .eq('user_id', userId)
        .eq('event_id', eventId);
      if (error) throw error;
      set((s) => ({
        rsvpEventIds: s.rsvpEventIds.filter((id) => id !== eventId),
      }));
    } catch (e) {
      console.error('Error removing rsvp:', e);
    }
  },

  isFavorited: (eventId: string) => {
    return get().favoriteEventIds.includes(eventId);
  },

  isRsvped: (eventId: string) => {
    return get().rsvpEventIds.includes(eventId);
  },

  toggleFavorite: async (eventId: string) => {
    const { isFavorited, addFavoriteEvent, removeFavoriteEvent } = get();
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    if (!userId) {
      console.warn('toggleFavorite: No authenticated user.');
      return;
    }
    if (isFavorited(eventId)) {
      await removeFavoriteEvent(userId, eventId);
    } else {
      await addFavoriteEvent(userId, eventId);
    }
  },

  toggleRsvp: async (eventId: string) => {
    const { isRsvped, addRsvp, removeRsvp } = get();
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    if (!userId) {
      console.warn('toggleRsvp: No authenticated user.');
      return;
    }
    if (isRsvped(eventId)) {
      await removeRsvp(userId, eventId);
    } else {
      await addRsvp(userId, eventId);
    }
  },

  resetState: () => {
    set({ favoriteEventIds: [], rsvpEventIds: [] });
  },
}));

export function useEventReactions() {
  const { user } = useAuth();
  const store = useEventReactionsStore();
  const [rsvpCounts, setRsvpCounts] = useState<Record<number, number>>({});
  const [favoriteCounts, setFavoriteCounts] = useState<Record<number, number>>({});

  useEffect(() => {
    if (user) {
      store.fetchFavoriteEvents(user.id);
      store.fetchRsvpEvents(user.id);
    } else {
      // Reset state when there is no authenticated user
      store.resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch counts from database
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch RSVP counts
        const { data: rsvpData, error: rsvpError } = await supabase
          .from('event_rsvps')
          .select('event_id');
        
        if (!rsvpError && rsvpData) {
          const counts: Record<number, number> = {};
          rsvpData.forEach((item) => {
            const eventId = parseInt(item.event_id, 10);
            if (!isNaN(eventId)) {
              counts[eventId] = (counts[eventId] || 0) + 1;
            }
          });
          setRsvpCounts(counts);
        }

        // Fetch favorite counts
        const { data: favoriteData, error: favoriteError } = await supabase
          .from('favorite_events')
          .select('event_id');
        
        if (!favoriteError && favoriteData) {
          const counts: Record<number, number> = {};
          favoriteData.forEach((item) => {
            const eventId = parseInt(item.event_id, 10);
            if (!isNaN(eventId)) {
              counts[eventId] = (counts[eventId] || 0) + 1;
            }
          });
          setFavoriteCounts(counts);
        }
      } catch (e) {
        console.error('Error fetching event counts:', e);
      }
    };

    fetchCounts();
  }, [store.favoriteEventIds, store.rsvpEventIds]);

  const getRsvpCount = (eventId: number): number => {
    return rsvpCounts[eventId] || 0;
  };

  const getFavoriteCount = (eventId: number): number => {
    return favoriteCounts[eventId] || 0;
  };

  return {
    ...store,
    rsvpCounts,
    favoriteCounts,
    getRsvpCount,
    getFavoriteCount,
  };
}
